import React, { useEffect, useRef } from "react"
import { toast } from "react-toastify"
import CabCard from "../../../components/Cards/CabCard"
import { cabAction } from "../../../store/Cab/CAbReducer"
import { useSelector, useDispatch } from "react-redux"
import Loading from "../../../components/Loading"

const CabBox = ({ tripDetails }) => {
  const dispatch = useDispatch()
  const { loading, cabs, error } = useSelector((state) => state.cab)
  const lastToastErrorRef = useRef(null)

  const isRateLimitError =
    error &&
    typeof error === "string" &&
    (error.includes("Rate limit") || error.includes("Maximum"))

  const isDateMissingError =
    error === "Please select return date and time to see cabs."

  useEffect(() => {
    if (error && lastToastErrorRef.current !== error && !isDateMissingError) {
      lastToastErrorRef.current = error
      toast.error(
        isRateLimitError
          ? "You've exceeded the request limit. Please try again after a few hours."
          : error
      )
    }

    if (!error) lastToastErrorRef.current = null
  }, [error, isRateLimitError, isDateMissingError])

  useEffect(() => {
    const loadStoredCabResult = () => {
      const stored = localStorage.getItem("cabSearchResult")

      if (!stored) return

      try {
        const parsed = JSON.parse(stored)

        // Only set result when API response has cab data.
        // This prevents route-page loading from staying stuck after Modify/Search.
        if (parsed && Array.isArray(parsed.data)) {
          dispatch(cabAction.setCabSearchResult(parsed))
        }
      } catch {
        localStorage.removeItem("cabSearchResult")
      }
    }

    loadStoredCabResult()

    // UpdateOutstationForm dispatches this event after Modify/Search.
    // Same route component does not always remount, so CabBox must listen manually.
    window.addEventListener("tripDataUpdated", loadStoredCabResult)
    window.addEventListener("storage", loadStoredCabResult)

    return () => {
      window.removeEventListener("tripDataUpdated", loadStoredCabResult)
      window.removeEventListener("storage", loadStoredCabResult)
    }
  }, [dispatch, tripDetails])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className="cabCardBox">
          {cabs?.length > 0 ? (
            <div className="cabCardBox__list">
              {[...cabs]
                .sort((a, b) => (a.vehicle_id || 0) - (b.vehicle_id || 0))
                .map((cab) => (
                  <CabCard
                    key={cab?.id || cab?.vehicle_id}
                    showPrice={true}
                    cab={cab}
                  />
                ))}
            </div>
          ) : isRateLimitError ? (
            <div className="cabCardBox__limitMsg">
              You've exceeded the request limit. Please try again after a few hours.
            </div>
          ) : isDateMissingError ? (
            <div
              className="cabCardBox__limitMsg"
              style={{
                textAlign: "center",
                padding: "20px",
                color: "red",
                fontWeight: "bold",
              }}
            >
              {error}
            </div>
          ) : (
            <div className="w-full text-center py-10 px-6">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#f0f6f5] flex items-center justify-center">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#21A5A0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 17h14M5 17a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-3h8l2 3h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2M5 17v2m14-2v2" /><circle cx="7.5" cy="17" r="0.5" /><circle cx="16.5" cy="17" r="0.5" />
                </svg>
              </div>
              <p className="text-[16px] font-black text-[#16211f] mb-1">
                {Number(tripDetails?.trip_type) === 2
                  ? `Hourly rentals aren't available in ${tripDetails?.pickup_city || "this city"} yet`
                  : "No cabs available for this search"}
              </p>
              <p className="text-[13.5px] text-[#6b7280] max-w-[420px] mx-auto mb-5">
                {Number(tripDetails?.trip_type) === 2
                  ? "We're expanding rental coverage. Try a local or outstation trip, or call us and we'll arrange it."
                  : "Please try different trip details, or call us and we'll help you book."}
              </p>
              <a
                href="tel:+918052080501"
                className="inline-flex items-center gap-2 rounded-xl bg-[#21A5A0] text-white font-bold text-[14px] px-5 py-3 hover:bg-[#1D8D89] transition-colors no-underline"
              >
                Call +91 80520 80501
              </a>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default CabBox